import { useCallback, useMemo, useState } from "react";

import { AlertTriangle, ArrowDown, ArrowUp, Check, Plus, RefreshCw } from "lucide-react";

import { DataTable, type DataTableColumn } from "@/components/cinch/DataTable/DataTable";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useCan } from "@/lib/auth";

import {
  useBucketRules,
  useCashBuckets,
  useCreateRule,
  useReapplyRules,
  useReorderRules,
  useRulePreview,
  useUpdateRule,
  type BucketRule,
} from "../api";

/** Add a mapping rule.
 *
 *  New rules append to the end of the evaluation order, which is the safe
 *  default: because rules are first-match-wins, appending cannot change how
 *  anything currently classifies. (Classifying from the unmapped queue is the
 *  opposite case — there the new rule is deliberately slotted ahead of
 *  whatever was shadowing it.)
 */
function AddRuleDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { data: buckets } = useCashBuckets();
  const create = useCreateRule();
  const [pattern, setPattern] = useState("");
  const [bucketId, setBucketId] = useState("");
  const [notes, setNotes] = useState("");

  const preview = useRulePreview(pattern, open);
  const canSave = pattern.trim() && bucketId && preview.data?.valid;

  const save = () => {
    if (!canSave) return;
    create.mutate(
      { pattern: pattern.trim(), bucket_id: bucketId, notes: notes.trim() || null, why: notes.trim() || null },
      {
        onSuccess: () => {
          setPattern("");
          setBucketId("");
          setNotes("");
          onClose();
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Add mapping rule</DialogTitle>
          <DialogDescription>
            Appended to the end of the evaluation order, so it can't change how anything
            already classifies. Use the unmapped queue's Classify action when you need a rule
            to take precedence over an existing one.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="rule-pattern">Pattern (regex, case-insensitive)</Label>
            <Input
              id="rule-pattern"
              value={pattern}
              onChange={(e) => setPattern(e.target.value)}
              placeholder="e.g. Acme\s+Supply"
              className="font-mono text-xs"
            />
            {preview.data && !preview.data.valid && (
              <p className="text-sm text-danger">Invalid: {preview.data.error}</p>
            )}
            {preview.data?.valid && (
              <div className="flex flex-col gap-1 text-xs">
                <p className="inline-flex items-center gap-1.5 text-muted-foreground">
                  <Check className="h-3.5 w-3.5 text-success" />
                  Matches <b className="text-foreground">{preview.data.match_count}</b>{" "}
                  transaction{preview.data.match_count === 1 ? "" : "s"}.
                </p>
                {preview.data.shadowed_by && (
                  <p className="inline-flex items-start gap-1.5 text-warning">
                    <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    <span>
                      An earlier rule{" "}
                      <code className="rounded bg-muted px-1">{preview.data.shadowed_by}</code>{" "}
                      already matches these, so appending this one will have no effect on them.
                    </span>
                  </p>
                )}
                {preview.data.samples.slice(0, 3).map((s) => (
                  <p key={s} className="truncate font-mono text-muted-foreground" title={s}>
                    {s}
                  </p>
                ))}
              </div>
            )}
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="rule-bucket">Cash bucket</Label>
            <Select value={bucketId} onValueChange={setBucketId}>
              <SelectTrigger id="rule-bucket">
                <SelectValue placeholder="Pick a bucket…" />
              </SelectTrigger>
              <SelectContent>
                {(buckets ?? []).map((b) => (
                  <SelectItem key={b.id} value={b.id}>
                    {b.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="rule-notes">Why / provenance</Label>
            <Textarea
              id="rule-notes"
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Cody confirmed 2026-08-06 — SMS registry, sits with Twilio"
            />
          </div>
          {create.isError && (
            <p className="text-sm text-danger">Failed: {create.error.message}</p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={!canSave || create.isPending} onClick={save}>
            {create.isPending ? "Adding…" : "Add rule"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** The workbook's Mapping tab: description patterns → cash buckets.
 *
 *  Order is the whole game. Rules are evaluated top to bottom and the first
 *  match wins, which is why the sweep-specific "ACH OFFSET CINCH DEBITS BofA
 *  trans" rules have to sit above the general Customer Collections rule —
 *  otherwise every internal transfer books as revenue.
 */
export function MappingTab() {
  const { data: rules, isLoading } = useBucketRules();
  const reorder = useReorderRules();
  const update = useUpdateRule();
  const reapply = useReapplyRules();
  const canWrite = useCan("cash_flow.write");
  const [filter, setFilter] = useState("");
  const [adding, setAdding] = useState(false);

  const ordered = useMemo(() => rules ?? [], [rules]);
  const shown = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return ordered;
    return ordered.filter(
      (r) =>
        r.pattern.toLowerCase().includes(q) ||
        (r.bucket_name ?? "").toLowerCase().includes(q) ||
        (r.notes ?? "").toLowerCase().includes(q),
    );
  }, [ordered, filter]);

  /** Swap a rule with its neighbour and persist the whole order.
   *
   *  The reorder endpoint takes every id, so the client sends the full list
   *  rather than a delta — that way a stale view can't silently drop a rule.
   */
  const move = useCallback(
    (rule: BucketRule, delta: number) => {
      const index = ordered.findIndex((r) => r.id === rule.id);
      const target = index + delta;
      if (index < 0 || target < 0 || target >= ordered.length) return;
      const next = [...ordered];
      const [moved] = next.splice(index, 1);
      next.splice(target, 0, moved!);
      reorder.mutate({
        ordered_ids: next.map((r) => r.id),
        why: `Moved ${rule.pattern} ${delta < 0 ? "earlier" : "later"} in evaluation order`,
      });
    },
    [ordered, reorder],
  );

  const columns = useMemo<DataTableColumn<BucketRule>[]>(
    () => [
      { key: "priority", header: "#", numeric: true, align: "right" },
      {
        key: "pattern",
        header: "Pattern",
        render: (r) => (
          <span
            className={`block max-w-sm truncate font-mono text-xs ${r.is_active ? "" : "line-through opacity-50"}`}
            title={r.pattern}
          >
            {r.pattern}
          </span>
        ),
      },
      {
        key: "bucket_name",
        header: "Bucket",
        render: (r) => r.bucket_name ?? "—",
      },
      {
        key: "notes",
        header: "Why / provenance",
        render: (r) => (
          <span className="block max-w-sm truncate text-xs text-muted-foreground" title={r.notes ?? ""}>
            {r.notes ?? "—"}
          </span>
        ),
      },
      {
        key: "is_active",
        header: "",
        align: "right",
        render: (r) => (
          <div className="flex items-center justify-end gap-1">
            <Button
              variant="ghost"
              size="sm"
              disabled={!canWrite || reorder.isPending || Boolean(filter)}
              title={filter ? "Clear the filter to reorder" : "Move earlier"}
              onClick={() => move(r, -1)}
            >
              <ArrowUp className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              disabled={!canWrite || reorder.isPending || Boolean(filter)}
              title={filter ? "Clear the filter to reorder" : "Move later"}
              onClick={() => move(r, 1)}
            >
              <ArrowDown className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              disabled={!canWrite || update.isPending}
              onClick={() =>
                update.mutate({
                  id: r.id,
                  is_active: !r.is_active,
                  why: `${r.is_active ? "Disabled" : "Re-enabled"} ${r.pattern}`,
                })
              }
            >
              {r.is_active ? "Disable" : "Enable"}
            </Button>
          </div>
        ),
      },
    ],
    [canWrite, filter, move, reorder.isPending, update],
  );

  return (
    <div className="flex flex-col gap-6">
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <CardTitle>
                Mapping rules
                {rules && <Badge variant="neutral" className="ml-2">{rules.length}</Badge>}
              </CardTitle>
              <CardDescription>
                Bank description → cash bucket. Evaluated top to bottom, <b>first match wins</b>
                {" "}— which is why the sweep-specific rules sit above the general Customer
                Collections rule. Get that order wrong and internal transfers book as revenue.
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={!canWrite || reapply.isPending}
                onClick={() => reapply.mutate()}
              >
                <RefreshCw className={`h-4 w-4 ${reapply.isPending ? "animate-spin" : ""}`} />
                Re-apply to history
              </Button>
              <Button size="sm" disabled={!canWrite} onClick={() => setAdding(true)}>
                <Plus className="h-4 w-4" />
                Add rule
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          {reapply.isSuccess && (
            <p className="text-sm text-muted-foreground">
              Scanned {reapply.data.scanned.toLocaleString()} transactions ·{" "}
              <b className="text-foreground">{reapply.data.rebucketed.toLocaleString()}</b>{" "}
              re-bucketed.
            </p>
          )}
          <Input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter by pattern, bucket, or note…"
            className="max-w-sm"
          />
          <DataTable
            columns={columns}
            rows={shown}
            rowKey={(r) => r.id}
            loading={isLoading}
            emptyMessage="No rules match that filter."
          />
        </CardContent>
      </Card>
      <AddRuleDialog open={adding} onClose={() => setAdding(false)} />
    </div>
  );
}
